import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import { toApiFailure } from "../../lib/errors";
import { formatDateTime } from "../../lib/format";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { downloadDsgvoExport, useDsgvoOverview } from "./api";
import { canManageDsgvoRequests } from "./permissions";
import { ManageRequestsTab, type ElevateSession } from "./manage-requests-tab";

/**
 * DSGVO-Auskunft / Loeschung (NF-033, [F-024]). Two tabs: "Meine Daten" is
 * self-service for EVERY authenticated user (data-category overview + Art.-15
 * export); "Anfragen verwalten" (search, occurrences, Auskunft, erasure,
 * manual review, Loeschjournal) shows only where `/me.can` holds
 * `dsgvo.search` - the DSGVO-Auditor. The nav link is therefore visible to
 * everyone. The server re-checks every route (ADR-API-09).
 *
 * `onNeedsElevation` reaches the erasure order: the elevation dialog of the
 * app shell, asked when the server wants an elevated session ([F-033]).
 */
export function DsgvoPage({ onNeedsElevation }: { onNeedsElevation?: ElevateSession } = {}): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const showManage = canManageDsgvoRequests(user);

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("dsgvo.title")}
        </h1>
        <p className="text-sm text-muted-foreground">{t("dsgvo.subtitle")}</p>
      </div>

      <Tabs defaultValue="my-data">
        <TabsList data-testid="dsgvo_tabs">
          <TabsTrigger value="my-data" data-testid="dsgvo_tab-my-data">
            {t("dsgvo.tabMyData")}
          </TabsTrigger>
          {showManage ? (
            <TabsTrigger value="manage" data-testid="dsgvo_tab-manage">
              {t("dsgvo.tabManage")}
            </TabsTrigger>
          ) : null}
        </TabsList>
        <TabsContent value="my-data" className="mt-4">
          <MyDataTab />
        </TabsContent>
        {showManage ? (
          <TabsContent value="manage" className="mt-4">
            <ManageRequestsTab onNeedsElevation={onNeedsElevation} />
          </TabsContent>
        ) : null}
      </Tabs>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * Tab "Meine Daten": calm card list of the subject's stored data categories
 * (GET /dsgvo/me), the primary (non-destructive, Ryddion-Orange) Art.-15
 * export button and the subtle audit-trail notice (NF-030).
 */
function MyDataTab(): JSX.Element {
  const { t } = useTranslation();
  const overview = useDsgvoOverview();
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const onExport = async (): Promise<void> => {
    setIsExporting(true);
    setExportError(null);
    try {
      await downloadDsgvoExport();
      toast.success(t("dsgvo.myData.exportDone"));
    } catch (err) {
      const failure = toApiFailure(err);
      setExportError(failure.problem?.detail ?? t("dsgvo.myData.exportError"));
    } finally {
      setIsExporting(false);
    }
  };

  if (overview.isError) {
    return <ErrorBanner onRetry={() => void overview.refetch()} />;
  }

  if (overview.isLoading || !overview.data) {
    return (
      <div className="max-w-2xl space-y-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={index}
            className="h-28 w-full animate-pulse rounded-xl border bg-muted"
          />
        ))}
      </div>
    );
  }

  const { account, counts } = overview.data;
  const categories = [
    { labelKey: "dsgvo.myData.catIncidents", count: counts.incidentsReported },
    { labelKey: "dsgvo.myData.catTimeline", count: counts.timelineEvents },
    { labelKey: "dsgvo.myData.catPostmortems", count: counts.postmortems },
    { labelKey: "dsgvo.myData.catAttachments", count: counts.attachments },
    { labelKey: "dsgvo.myData.catAudit", count: counts.auditReferences },
  ];

  return (
    <div className="max-w-2xl space-y-4">
      <p className="text-sm text-muted-foreground">{t("dsgvo.myData.intro")}</p>

      <Card data-testid="dsgvo_my-account">
        <CardHeader>
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t("dsgvo.myData.account")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{account.displayName}</span>
            <Badge variant="outline">
              {account.active
                ? t("dsgvo.myData.accountActive")
                : t("dsgvo.myData.accountInactive")}
            </Badge>
          </div>
          <p className="text-muted-foreground">
            {t("dsgvo.myData.email")}: {account.email ?? t("common.none")}
          </p>
          <p className="text-muted-foreground">
            {t("dsgvo.myData.accountCreatedAt")}:{" "}
            {formatDateTime(account.createdAt)}
          </p>
        </CardContent>
      </Card>

      <Card data-testid="dsgvo_my-categories">
        <CardHeader>
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t("dsgvo.myData.categories")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="divide-y">
            {categories.map((category) => (
              <li
                key={category.labelKey}
                className="flex items-baseline justify-between gap-3 py-2 text-sm"
              >
                <span>{t(category.labelKey)}</span>
                <span className="whitespace-nowrap text-muted-foreground tabular-nums">
                  {category.count === 1
                    ? t("dsgvo.myData.countOne")
                    : t("dsgvo.myData.countMany", { count: category.count })}
                </span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {exportError ? (
        <div
          role="alert"
          className="flex items-center justify-between gap-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm"
        >
          <span>{exportError}</span>
          <Button
            variant="outline"
            size="sm"
            disabled={isExporting}
            onClick={() => void onExport()}
          >
            {t("common.tryAgain")}
          </Button>
        </div>
      ) : null}

      <div className="space-y-3">
        {/* Primary = Ryddion-Orange: the export is the NON-destructive main
         * action of this tab (M-008); min 44px touch target on mobile. */}
        <Button
          data-testid="dsgvo_my-export"
          className="h-11 sm:h-9"
          disabled={isExporting}
          onClick={() => void onExport()}
        >
          {isExporting ? (
            <Loader2 aria-hidden="true" className="animate-spin" />
          ) : (
            <Download aria-hidden="true" />
          )}
          {isExporting
            ? t("dsgvo.myData.exportRunning")
            : t("dsgvo.myData.export")}
        </Button>
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <ShieldCheck aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
          {t("dsgvo.myData.auditNotice")}
        </p>
      </div>
    </div>
  );
}

function ErrorBanner({ onRetry }: { onRetry: () => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      className="flex items-center justify-between gap-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm"
    >
      <span>{t("errors.load")}</span>
      <Button variant="outline" size="sm" onClick={onRetry}>
        {t("common.retry")}
      </Button>
    </div>
  );
}
